import React from 'react';
import { Search, Settings, RefreshCw, Calendar } from 'lucide-react';
import { US_STATES, US_TIMEZONES } from '../../lib/usConstants.js';
import MultiSelectDropdown from '../../components/MultiSelectDropdown.jsx';
import OnlyShowDropdown from './OnlyShowDropdown.jsx';
import { PAGE_SIZE_OPTIONS } from './leadsConstants.jsx';
import { LEAD_STATUS_OPTIONS } from './leadFilterOptions.js';
import SavedSearchesMenu from './SavedSearchesMenu.jsx';

export default function LeadsFilterBar({
  dateFrom, setDateFrom,
  dateTo, setDateTo,
  sort, setSort,
  pageSize, setPageSize,
  vendors, setVendors, vendorOptions,
  states, setStates,
  timezones, setTimezones,
  onlyShow, setOnlyShow,
  onlyShowDays, setOnlyShowDays,
  onlyShowZip, setOnlyShowZip,
  onlyShowBirthYear, setOnlyShowBirthYear,
  onlyShowBirthMonth, setOnlyShowBirthMonth,
  dispositionTags, setDispositionTags, dispositionOptions,
  excludeDispositions, setExcludeDispositions,
  search, setSearch,
  statuses, setStatuses,
  savedSnapshot, onApplySaved,
  setPage,
  onManageDispositions,
  onResetFilters,
}) {
  return (
    <div className="panel p-4 space-y-4 mt-2">
      {/* Row 1: date range + sort + page size */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">Leads from</label>
          <div className="relative">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="input pr-8"
            />
            {dateFrom && (
              <button
                onClick={() => setDateFrom('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-red-500/80 text-white text-[10px] flex items-center justify-center"
              >×</button>
            )}
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">Leads to</label>
          <div className="relative">
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="input pr-8"
            />
            {dateTo && (
              <button
                onClick={() => setDateTo('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-red-500/80 text-white text-[10px] flex items-center justify-center"
              >×</button>
            )}
          </div>
        </div>
        <div className="md:col-span-3 flex items-end">
          <span className="text-[11px] uppercase tracking-wider text-civix-purpleSoft/80 font-semibold flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Date field — Received on
          </span>
        </div>
        <div className="md:col-span-3">
          <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">Sort</label>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="input"
          >
            <option value="-createdAt">Date received — newest first</option>
            <option value="createdAt">Date received — oldest first</option>
            <option value="-updatedAt">Last updated — newest</option>
            <option value="updatedAt">Last updated — oldest</option>
            <option value="lastName">Last name (A→Z)</option>
            <option value="-lastContactedAt">Last contacted — newest</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-white/40 mb-1">Per page</label>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
            className="input"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 2: vendors / states / timezones */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MultiSelectDropdown
          label="Lead vendors"
          options={vendorOptions}
          value={vendors}
          onChange={(v) => { setVendors(v); setPage(1); }}
        />
        <MultiSelectDropdown
          label="States"
          options={US_STATES.map((s) => ({ value: s.code, label: `${s.code} — ${s.name}` }))}
          value={states}
          onChange={(v) => { setStates(v); setPage(1); }}
        />
        <MultiSelectDropdown
          label="Time zones"
          options={US_TIMEZONES.map((t) => ({ value: t.id, label: t.label }))}
          value={timezones}
          onChange={(v) => { setTimezones(v); setPage(1); }}
        />
      </div>

      {/* Row 3: only show + lead statuses + disposition tags */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <OnlyShowDropdown
          value={onlyShow}
          onChange={(v) => { setOnlyShow(v); setPage(1); }}
          days={onlyShowDays}
          setDays={setOnlyShowDays}
          zip={onlyShowZip}
          setZip={setOnlyShowZip}
          year={onlyShowBirthYear}
          setYear={setOnlyShowBirthYear}
          month={onlyShowBirthMonth}
          setMonth={setOnlyShowBirthMonth}
        />
        <MultiSelectDropdown
          label="Lead statuses"
          options={LEAD_STATUS_OPTIONS}
          value={statuses}
          onChange={(v) => { setStatuses(v); setPage(1); }}
        />
        <MultiSelectDropdown
          label="Disposition tags"
          options={dispositionOptions}
          value={dispositionTags}
          onChange={(v) => { setDispositionTags(v); setPage(1); }}
        />
      </div>

      {/* Row 4: exclude + search */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MultiSelectDropdown
          label="Exclude disposition tags"
          options={dispositionOptions}
          value={excludeDispositions}
          onChange={(v) => { setExcludeDispositions(v); setPage(1); }}
        />
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Enter name, phone number or email"
            className="input pl-9"
          />
        </div>
      </div>

      {/* Row 5: utility — manage dispositions + reset */}
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-4">
          <button
            onClick={onManageDispositions}
            className="text-[11px] text-civix-purpleSoft hover:text-white flex items-center gap-1"
          >
            <Settings className="w-3 h-3" /> Manage dispositions
          </button>
          <SavedSearchesMenu current={savedSnapshot} onApply={onApplySaved} />
        </div>
        <button
          onClick={onResetFilters}
          className="text-[11px] text-white/50 hover:text-white flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> Reset filters
        </button>
      </div>
    </div>
  );
}
